import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { ProjectId } from 'api/models';
import { FieldValueType, Task, TaskAPI } from 'api/tasks';
import { CpBody01Link } from 'components/atoms/customer-portal/typography';
import { computeSectionFieldMapping } from 'components/organisms/field-sections/utils';
import {
  canShowInTaskFieldsSettings,
  taskField,
} from 'components/organisms/module-settings/fields/utils';
import { addValuesToFields } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields';
import { usePopulateUsersMetaInFields } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields/use-task-custom-fields';
import { useAllFieldsQuery } from 'queries';
import { useFieldSectionsQuery } from 'queries/field-sections';
import { CPTaskDrawerFieldSection } from './field-section';

export interface CpTaskDrawerFieldAndValuesSectionProps {
  task: Task;
}

const Wrapper = styled.div`
  /* padding-top: var(--cp-spacing-05); */
  width: 100%;
  /* border-top: 1px solid var(--gray-10); */
`;

const AdditionalInfoTriggerWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  cursor: pointer;
  width: 100%;

  user-select: none;

  svg {
    margin-left: var(--spacing-03);
    fill: var(--cp-link-primary);
  }
`;

export const CpTaskDrawerFieldAndValuesSection: React.FC<CpTaskDrawerFieldAndValuesSectionProps> =
  ({ task }) => {
    const [isExpanded, setExpanded] = useState(true);
    const { data: fieldsList } = useAllFieldsQuery();

    const [dirtyState, setDirtyState] = useState(task?.fields ?? []);

    const taskFieldsList = useMemo(() => {
      return fieldsList
        ? fieldsList
            .filter(taskField)
            .filter(canShowInTaskFieldsSettings)
            .filter((field) => field.isEnabled)
        : [];
    }, [fieldsList]);

    const taskFields = usePopulateUsersMetaInFields(
      taskFieldsList ?? [],
      ProjectId(task?.project?.projectId),
    );

    const { data: fieldSections } = useFieldSectionsQuery(
      FieldSectionType.Task,
    );

    const fieldsWithValues = useMemo(() => {
      return addValuesToFields(taskFields, dirtyState);
    }, [taskFields, dirtyState]);

    const fieldSectionMapping = useMemo(() => {
      const customSections = fieldSections?.filter((section) => !section.fixed);
      const sectionFieldMapping = computeSectionFieldMapping(
        fieldsWithValues,
        customSections,
      );

      return sectionFieldMapping;
    }, [fieldsWithValues, fieldSections]);

    useEffect(() => {
      setDirtyState(task?.fields ?? []);
    }, [task?.fields]);

    return (
      <>
        <AdditionalInfoTriggerWrapper onClick={() => setExpanded((p) => !p)}>
          <CpBody01Link>
            <FormattedMessage id="additional_details" />
          </CpBody01Link>
          {isExpanded ? <ChevronUp16 /> : <ChevronDown16 />}
        </AdditionalInfoTriggerWrapper>
        <Wrapper>
          {isExpanded ? (
            fieldSectionMapping.map((fs) => (
              <CPTaskDrawerFieldSection
                fieldSection={fs}
                onChange={(c, f) => {
                  const prevValue = dirtyState.find(
                    (field) => field.fieldId === f.fieldId,
                  )?.fieldValue;

                  if (_.isEqual(prevValue, c)) {
                    return;
                  }

                  setDirtyState((p) => {
                    return p.map((field) => {
                      if (field.fieldId === f.fieldId) {
                        return { ...field, fieldValue: c as FieldValueType };
                      }

                      return field;
                    });
                  });
                  TaskAPI.updateTask(task?.project?.projectId, task?.taskId, {
                    fields: [{ fieldId: f.fieldId, fieldValue: c }],
                  }).then((resp) => {
                    setDirtyState(resp?.fields ?? []);
                  });
                }}
              />
            ))
          ) : (
            <></>
          )}
        </Wrapper>
      </>
    );
  };
